import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CreditCard, Plus, CheckCircle, Loader2 } from "lucide-react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "../ui/Button";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { apiService } from "../../services/paymentAPI";
import { PaymentMethod } from "../../types/payment";
import { GlobalService } from "../../modules";
import { useTranslation } from "react-i18next";

const CARD_ELEMENT_OPTIONS = {
	hidePostalCode: true,
	style: {
		base: {
			fontSize: "16px",
			color: "#424770",
			"::placeholder": {
				color: "#aab7c4",
			},
		},
		invalid: {
			color: "#9e2146",
		},
	},
};

interface PaymentMethodSelectorProps {
	onPaymentMethodSelected: (paymentMethodId: string, isNewCard: boolean) => void;
	loading?: boolean;
	selectedPaymentMethodId?: string | null;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
	onPaymentMethodSelected,
	loading = false,
	selectedPaymentMethodId
}) => {
	const { t } = useTranslation();
	const stripe = useStripe();
	const elements = useElements();
	const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
	const [loadingMethods, setLoadingMethods] = useState(true);
	const [selectedMethod, setSelectedMethod] = useState<string | null>(selectedPaymentMethodId || null);
	const [showNewCardForm, setShowNewCardForm] = useState(false);
	const [saveCard, setSaveCard] = useState(true);
	const [cardError, setCardError] = useState<string | null>(null);
	const [processingNewCard, setProcessingNewCard] = useState(false);

	useEffect(() => {
		fetchPaymentMethods();
	}, []);

	const fetchPaymentMethods = async () => {
		try {
			setLoadingMethods(true);
			const response = await apiService.getMyPaymentMethods();
			const data = response.data || response;
			const methods = Array.isArray(data) ? data : [];
			
			// Filter to only show card payment methods
			const cardMethods = methods.filter((method: PaymentMethod) => method.type === "card");
			setPaymentMethods(cardMethods);

			// Auto-select default payment method if exists
			const defaultMethod = cardMethods.find((method: PaymentMethod) => method.isDefault);
			if (defaultMethod && !selectedPaymentMethodId) {
				setSelectedMethod(defaultMethod.id);
				onPaymentMethodSelected(defaultMethod.id, false);
			} else if (cardMethods.length === 0) {
				// If no cards, show new card form
				setShowNewCardForm(true);
			}
		} catch (error) {
			console.error("Error fetching payment methods:", error);
		} finally {
			setLoadingMethods(false);
		}
	};

	const handleSelectExistingCard = (paymentMethodId: string) => {
		setSelectedMethod(paymentMethodId);
		setShowNewCardForm(false);
		onPaymentMethodSelected(paymentMethodId, false);
	};

	const handleSelectNewCard = () => {
		setSelectedMethod("new");
		setShowNewCardForm(true);
	};

	const handleNewCardSubmit = async () => {
		if (!stripe || !elements) {
			return;
		}

		setProcessingNewCard(true);
		setCardError(null);

		try {
			const cardElement = elements.getElement(CardElement);
			if (!cardElement) {
				throw new Error(t("paymentMethodSelector.cardElementNotFound"));
			}

			if (saveCard) {
				// Create setup intent to save the card
				const setupIntentResponse = await apiService.createSetupIntent();
				const { client_secret } = setupIntentResponse;

				// Confirm setup intent
				const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
					client_secret,
					{
						payment_method: {
							card: cardElement,
						},
					}
				);

				if (stripeError) {
					setCardError(stripeError.message || t("paymentMethodSelector.cardValidationFailed"));
					setProcessingNewCard(false);
					return;
				}

				if (setupIntent?.status === "succeeded") {
					// Save payment method to backend
					const savedPaymentMethod = await apiService.createPaymentMethod({
						stripePaymentMethodId: setupIntent.payment_method as string,
						type: "card",
						isDefault: paymentMethods.length === 0, // Set as default if first card
					});

					// Refresh payment methods to get the updated list
					const response = await apiService.getMyPaymentMethods();
					const data = response.data || response;
					const methods = Array.isArray(data) ? data : [];
					const cardMethods = methods.filter((method: PaymentMethod) => method.type === "card");
					setPaymentMethods(cardMethods);

					// Find the newly created payment method
					// It should be the one with the matching Stripe payment method ID
					const newlyCreatedMethod = cardMethods.find(
						(method: PaymentMethod) => method.stripePaymentMethodId === setupIntent.payment_method
					);

					if (newlyCreatedMethod) {
						// Select the newly created payment method
						setSelectedMethod(newlyCreatedMethod.id);
						setShowNewCardForm(false);
						onPaymentMethodSelected(newlyCreatedMethod.id, false);
						console.log("Newly created payment method selected:", newlyCreatedMethod.id);
						
						// Show success message
						GlobalService.showToast(
							"success",
							t("paymentMethodSelector.cardSaved"),
							t("paymentMethodSelector.cardSavedMessage")
						);
					} else {
						// Fallback: use the Stripe payment method ID if we can't find the backend method
						console.warn("Could not find newly created payment method in list, using Stripe ID");
						onPaymentMethodSelected(setupIntent.payment_method as string, false);
						setShowNewCardForm(false);
						setSelectedMethod(setupIntent.payment_method as string);
						
						// Show success message
						GlobalService.showToast(
							"success",
							t("paymentMethodSelector.cardAdded"),
							t("paymentMethodSelector.cardReadyMessage")
						);
					}
				}
			} else {
				// Create payment method without saving
				const { error, paymentMethod } = await stripe.createPaymentMethod({
					type: "card",
					card: cardElement,
				});

				if (error) {
					setCardError(error.message || t("paymentMethodSelector.cardValidationFailed"));
					setProcessingNewCard(false);
					return;
				}

				// Use the payment method directly
				onPaymentMethodSelected(paymentMethod!.id, true);
				setShowNewCardForm(false);
				
				// Show success message
				GlobalService.showToast(
					"success",
					t("paymentMethodSelector.cardValidated"),
					t("paymentMethodSelector.cardReadyOneTimeMessage")
				);
			}
		} catch (err: any) {
			setCardError(err.message || t("paymentMethodSelector.failedToProcessCard"));
		} finally {
			setProcessingNewCard(false);
		}
	};

	if (loadingMethods) {
		return (
			<Card className="p-6">
				<div className="flex items-center justify-center py-8">
					<Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-3" />
					<span className="text-gray-600">{t("paymentMethodSelector.loadingPaymentMethods")}</span>
				</div>
			</Card>
		);
	}

	return (
		<Card className="p-6">
			<div className="mb-4">
				<h3 className="text-lg font-semibold text-gray-900 mb-2">{t("paymentMethodSelector.selectPaymentMethod")}</h3>
				<p className="text-sm text-gray-600">
					{t("paymentMethodSelector.chooseCard")}
				</p>
			</div>

			<div className="space-y-3">
				{/* Existing Payment Methods */}
				{paymentMethods.map((method) => (
					<motion.div
						key={method.id}
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
							selectedMethod === method.id && !showNewCardForm
								? "border-blue-500 bg-blue-50"
								: "border-gray-200 hover:border-gray-300"
						}`}
						onClick={() => !loading && handleSelectExistingCard(method.id)}
					>
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-3">
								<CreditCard className="h-5 w-5 text-gray-600" />
								<div>
									<div className="flex items-center gap-2">
										<span className="font-medium text-gray-900">
											{method.brand?.toUpperCase()} ****{method.last4}
										</span>
										{method.isDefault && (
											<Badge variant="default" className="bg-blue-100 text-blue-800 text-xs">{t("paymentMethodSelector.default")}</Badge>
										)}
									</div>
									<span className="text-sm text-gray-600">
										{t("paymentMethodSelector.expires")} {method.expMonth}/{method.expYear}
									</span>
								</div>
							</div>
							{selectedMethod === method.id && !showNewCardForm && (
								<CheckCircle className="h-5 w-5 text-blue-600" />
							)}
						</div>
					</motion.div>
				))}

				{/* New Card Option */}
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
						showNewCardForm
							? "border-blue-500 bg-blue-50"
							: "border-gray-200 hover:border-gray-300"
					}`}
					onClick={() => !loading && handleSelectNewCard()}
				>
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<Plus className="h-5 w-5 text-gray-600" />
							<div>
								<span className="font-medium text-gray-900">{t("paymentMethodSelector.addNewCard")}</span>
								<p className="text-sm text-gray-600">{t("paymentMethodSelector.useDifferentCard")}</p>
							</div>
						</div>
						{showNewCardForm && (
							<CheckCircle className="h-5 w-5 text-blue-600" />
						)}
					</div>
				</motion.div>

				{/* New Card Form */}
				<AnimatePresence>
					{showNewCardForm && (
						<motion.div
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							exit={{ opacity: 0, height: 0 }}
							className="overflow-hidden"
						>
							<div className="border-2 border-gray-200 rounded-lg p-4 mt-3 space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										{t("paymentMethodSelector.cardDetails")}
									</label>
									<div className="p-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
										<CardElement
											options={CARD_ELEMENT_OPTIONS}
											onChange={(event) => {
												setCardError(event.error ? event.error.message : null);
											}}
										/>
									</div>
									{cardError && (
										<p className="mt-2 text-sm text-red-600">{cardError}</p>
									)}
								</div>

								{/* Save Card Checkbox */}
								<div className="flex items-start">
									<div className="flex items-center h-5">
										<input
											id="save-card"
											type="checkbox"
											checked={saveCard}
											onChange={(e) => setSaveCard(e.target.checked)}
											className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
										/>
									</div>
									<div className="ml-3">
										<label htmlFor="save-card" className="font-medium text-gray-700 text-sm cursor-pointer">
											{t("paymentMethodSelector.saveCardForFuture")}
										</label>
										<p className="text-xs text-gray-500">
											{t("paymentMethodSelector.securelyStoredMessage")}
										</p>
									</div>
								</div>

								<Button
									type="button"
									onClick={handleNewCardSubmit}
									disabled={!stripe || processingNewCard || loading}
									className="w-full bg-blue-600 hover:bg-blue-700"
								>
									{processingNewCard ? (
										<>
											<Loader2 className="h-4 w-4 animate-spin mr-2" />
											{saveCard ? t("paymentMethodSelector.savingCard") : t("paymentMethodSelector.validatingCard")}
										</>
									) : (
										<>
											<CreditCard className="h-4 w-4 mr-2" />
											{saveCard ? t("paymentMethodSelector.saveAndUseCard") : t("paymentMethodSelector.useCard")}
										</>
									)}
								</Button>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</Card>
	);
};

export default PaymentMethodSelector;


